import { AI_CHAT_POLICY_MAX, aiChatSupport, defaultAiChatFor } from '../sessionSettings'

// Experiments: one exercise run as several conditions, each its own ordinary session.
//
// Built entirely on existing columns - no migration:
//   - The experiment itself is a session row of type 'Experiment' (the wizard's draft,
//     converted at publish). Its test_code.experiment holds the settings below plus each
//     condition's session id; its own test_code keys (template, unit_test, ...) stay as the
//     exercise every condition was copied from.
//   - Each condition is a normal session copied from that draft. Its experiment_group column
//     ties it back: 'exp:<experiment session id>:<condition key>'.
//
// In the wizard, everything every condition shares (problem, language, tests, starter code)
// is authored once in the regular steps; what can differ between conditions (AI chat, its
// policy, the offloading panel) is set per condition on the Experiment step.
//
// The backend (vizpi_backend/services/experimentConfig.js) validates the same limits; keep
// the two in step.

export const EXPERIMENT_TYPE = 'Experiment'

export const MIN_CONDITIONS = 2
// Nine: the classroom study runs A1-A8 and N as one experiment per course section.
export const MAX_CONDITIONS = 9
export const EXPERIMENT_NAME_MAX = 120
export const CONDITION_NAME_MAX = 40
export const MAX_CONCEPTS = 12
export const CONCEPT_LABEL_MAX = 80
export const CONCEPT_DESCRIPTION_MAX = 300
export const CONCEPT_EXAMPLE_MAX = 200

export const ASSIGNMENT_MODES = [
  {
    value: 'balanced',
    label: 'Balanced',
    description:
      'One link for everyone. Each student is placed in a condition when they first open it, so the groups stay the same size.',
  },
  {
    value: 'link',
    label: 'By link',
    description: 'Each condition has its own link, and you decide who gets which (e.g. one per section).',
  },
]

export const OFFLOADING_MODES = [
  { value: 'off', label: 'Off' },
  { value: 'hidden', label: 'Analyze only (students see nothing)' },
  { value: 'shown', label: 'Show the panel to students' },
]

export const assignmentLabel = (value) =>
  ASSIGNMENT_MODES.find((mode) => mode.value === value)?.label || value

export const offloadingLabel = (value) =>
  OFFLOADING_MODES.find((mode) => mode.value === value)?.label || value

// Client-only ids so condition and concept rows keep their identity (React keys) while they
// are renamed, reordered or removed. Stripped before publishing.
let nextRowId = 1
const rowId = () => `row-${nextRowId++}`

const conditionLetter = (index) => String.fromCharCode(65 + index)

// The first 'Condition X' no row uses yet (compared like validateExperiment does), so adding
// a condition after removing one never produces a duplicate default name.
const nextConditionName = (conditions) => {
  const used = new Set(conditions.map((condition) => condition.name.trim().toLowerCase()))
  for (let index = 0; ; index++) {
    const name = `Condition ${conditionLetter(index)}`
    if (!used.has(name.toLowerCase())) return name
  }
}

// aiChat: null means the exercise type's usual default (defaultAiChatFor), true/false is the
// instructor's choice for this condition. aiPolicy is this condition's AI Chat Policy.
// `existing` (the current rows) picks a name none of them has; `start` seeds the AI settings.
export const newCondition = (index, existing, start = {}) => ({
  rowId: rowId(),
  name: existing ? nextConditionName(existing) : `Condition ${conditionLetter(index)}`,
  aiChat: start.aiChat ?? null,
  aiPolicy: start.aiPolicy ?? '',
  offloading: 'off',
})

// `start` carries over what the AI Chat step already holds when the switch is turned on, so
// nothing set there is lost when that step gives way to per-condition settings.
export const newExperiment = (problemName, start = {}) => ({
  name: problemName || '',
  assignment: 'balanced',
  conditions: [newCondition(0, null, start), newCondition(1, null, start)],
  concepts: [],
})

// example: optional - the student's tooltip (offloading strip) shows it under the description.
export const newConcept = (label = '', description = '', example = '') => ({ rowId: rowId(), label, description, example })

// Whether students in this condition get the AI assistant. Types without a student AI chat
// never do, and the Initial/Final comparison exercises always do (see sessionSettings).
export const conditionAiChat = (condition, type) => {
  const support = aiChatSupport(type)
  if (support === 'unavailable') return false
  if (support === 'required') return true
  return condition.aiChat ?? defaultAiChatFor(type)
}

// The offloading mode a PUBLISHED condition runs ('off' | 'hidden' | 'shown'): the backend
// resolved chat and offloading per exercise type at publish, so its config is the answer.
export const publishedOffloading = (condition) => (condition?.aiChat && condition.offloading) || 'off'

// The policy and the offloading panel belong to the chat, so they are off wherever it is.
export const conditionPolicy = (condition, type) =>
  conditionAiChat(condition, type) ? (condition.aiPolicy || '').trim() : ''

export const conditionOffloading = (condition, type) =>
  conditionAiChat(condition, type) ? condition.offloading || 'off' : 'off'

// The concept list is only needed when some condition runs the offloading analysis.
export const usesConcepts = (experiment, type) =>
  Boolean(experiment?.conditions?.some((condition) => conditionOffloading(condition, type) !== 'off'))

const filledConcepts = (experiment) =>
  (experiment?.concepts || []).filter(
    (concept) => concept.label.trim() || concept.description.trim() || (concept.example || '').trim()
  )

// '' when the experiment can be published, otherwise what to fix (one message at a time).
export const validateExperiment = (experiment, type) => {
  if (!experiment) return 'Set up the experiment before publishing.'
  const name = experiment.name.trim()
  if (!name) return 'Give the experiment a name.'
  if (name.length > EXPERIMENT_NAME_MAX) return `Keep the experiment name under ${EXPERIMENT_NAME_MAX} characters.`

  const conditions = experiment.conditions || []
  if (conditions.length < MIN_CONDITIONS) return `An experiment needs at least ${MIN_CONDITIONS} conditions.`
  if (conditions.length > MAX_CONDITIONS) return `An experiment can have at most ${MAX_CONDITIONS} conditions.`
  const seen = new Set()
  for (const condition of conditions) {
    const conditionName = condition.name.trim()
    if (!conditionName) return 'Give every condition a name.'
    if (conditionName.length > CONDITION_NAME_MAX) {
      return `Keep condition names under ${CONDITION_NAME_MAX} characters.`
    }
    const key = conditionName.toLowerCase()
    if (seen.has(key)) return `Two conditions are both named "${conditionName}". Give each a different name.`
    seen.add(key)
    if (conditionPolicy(condition, type).length > AI_CHAT_POLICY_MAX) {
      return `Keep the AI Chat Policy of "${conditionName}" under ${AI_CHAT_POLICY_MAX} characters.`
    }
  }

  if (!ASSIGNMENT_MODES.some((mode) => mode.value === experiment.assignment)) {
    return 'Choose how students are assigned to conditions.'
  }

  if (usesConcepts(experiment, type)) {
    const concepts = filledConcepts(experiment)
    if (!concepts.length) {
      return 'The offloading panel needs a concept list. Add concepts, or use Draft with AI.'
    }
    if (concepts.length > MAX_CONCEPTS) return `Keep the concept list to ${MAX_CONCEPTS} concepts or fewer.`
    if (concepts.some((concept) => !concept.label.trim())) return 'Give every concept a name.'
    if (concepts.some((concept) => concept.label.trim().length > CONCEPT_LABEL_MAX)) {
      return `Keep concept names under ${CONCEPT_LABEL_MAX} characters.`
    }
    if (concepts.some((concept) => (concept.example || '').trim().length > CONCEPT_EXAMPLE_MAX)) {
      return `Keep concept examples under ${CONCEPT_EXAMPLE_MAX} characters.`
    }
    if (concepts.some((concept) => concept.description.trim().length > CONCEPT_DESCRIPTION_MAX)) {
      return `Keep concept descriptions under ${CONCEPT_DESCRIPTION_MAX} characters.`
    }
  }
  return ''
}

// The body for POST /experiments: every condition's effective settings, resolved here so the
// server stores exactly what the instructor saw in the wizard.
export const buildPublishPayload = (draftSessionId, experiment, type) => ({
  draftSessionId,
  name: experiment.name.trim(),
  assignment: experiment.assignment,
  conditions: experiment.conditions.map((condition) => ({
    name: condition.name.trim(),
    aiChat: conditionAiChat(condition, type),
    aiPolicy: conditionPolicy(condition, type),
    offloading: conditionOffloading(condition, type),
  })),
  concepts: usesConcepts(experiment, type)
    ? filledConcepts(experiment).map((concept) => ({
        label: concept.label.trim(),
        description: concept.description.trim(),
        example: (concept.example || '').trim(),
      }))
    : [],
})

// ---- running an experiment (backend: experimentConfig.planControl) ----

// Conditions whose own Session Control (on their session page) has taken them out of step
// with the experiment: a different status, or a different timer.
export const conditionsOutOfStep = (experimentRow, conditionRows) =>
  (conditionRows || []).filter((row) => {
    if (row.status !== experimentRow?.status) return true
    if (row.duration_minutes !== experimentRow?.duration_minutes) return true
    if (!row.started_at !== !experimentRow?.started_at) return true
    return Boolean(row.started_at) && Math.abs(Date.parse(row.started_at) - Date.parse(experimentRow.started_at)) > 2000
  })

// ---- reading published experiments ----

export const isExperimentSession = (session) => session?.type === EXPERIMENT_TYPE

export const experimentConfigOf = (session) =>
  isExperimentSession(session) ? session.test_code?.experiment || null : null

// Whether students join through the experiment's own link and are placed by it ('balanced'),
// rather than through each condition's link ('link'). The backend reads it the same way.
export const sharesOneLink = (session) =>
  isExperimentSession(session) && experimentConfigOf(session)?.assignment !== 'link'

const CONDITION_TAG = /^exp:(\d+):([a-z0-9]+)$/

// A condition session's experiment_group (the backend writes it at publish).
export const conditionTag = (experimentId, key) => `exp:${experimentId}:${key}`

// experiment_group of a condition session -> { experimentId, key }, or null for any other
// session (including rows from older studies that used the column for something else).
export const parseConditionTag = (value) => {
  const match = CONDITION_TAG.exec(String(value ?? ''))
  return match ? { experimentId: Number(match[1]), key: match[2] } : null
}

export const conditionTagOf = (session) => parseConditionTag(session?.experiment_group)

// The condition a session plays in `experimentSession`, or null.
export const conditionOf = (session, experimentSession) => {
  const tag = conditionTagOf(session)
  const config = experimentConfigOf(experimentSession)
  if (!tag || !config || Number(experimentSession.id) !== tag.experimentId) return null
  return config.conditions?.find((condition) => condition.key === tag.key) || null
}

// ---- the classroom study (backend: experimentConfig.normalizeExperimentStudy) ----
//
// An experiment published for the study carries config.study ({ studyKey, section,
// exerciseId, probeSet, requireAccount }) and each of its conditions condition.study
// ({ conditionId, ... }). Any other experiment has neither, and these read as empty.

// The study's id for a configured condition ('A1'-'A8', 'N'), or '' outside the study.
export const studyConditionId = (condition) => {
  const id = condition?.study?.conditionId
  return typeof id === 'string' ? id : ''
}

// Whether students opening this experiment must sign in with an account instead of joining
// as guests (Login.jsx then leaves out the guest button): a study follows each student
// across its exercises by their account.
export const requiresStudentAccount = (experimentSession) =>
  experimentConfigOf(experimentSession)?.study?.requireAccount === true
